/**
 * Push в нашем Flutter-приложении — по токену (docs/notes/flutter-bridge.md ›
 * Push). Три дела: закрепить токен телефона за вошедшим покупателем (при каждом
 * запуске с сессией и после входа), повторить это, когда Firebase сменил токен,
 * и отвязать телефон перед выходом — иначе он получал бы заказы прежнего
 * покупателя. В браузере и Telegram оболочки нет, и всё здесь ничего не делает.
 *
 * Ни одно из дел не мешает входу и выходу: сбой только пишется в консоль.
 */
import type { AuthApi } from '../features/auth'
import {
  NATIVE_PUSH_RECEIVED_EVENT,
  NATIVE_PUSH_TOKEN_EVENT,
  nativePushToken,
  toPushToken,
} from './nativeBridge'

/** Какой токен закрепили: при выходе отвязываем ровно его, оболочку не спрашиваем. */
export const PUSH_TOKEN_KEY = 'dorixona.pushToken'

/** Дольше выход не ждёт: оболочка ждёт подтверждения выхода всего 8 секунд. */
export const PUSH_UNREGISTER_TIMEOUT = 3000

type PushApi = Pick<AuthApi, 'registerPushToken' | 'unregisterPushToken'>

function stored(): string | null {
  try {
    return window.localStorage.getItem(PUSH_TOKEN_KEY)
  } catch {
    return null
  }
}

function store(token: string | null) {
  try {
    if (token === null) window.localStorage.removeItem(PUSH_TOKEN_KEY)
    else window.localStorage.setItem(PUSH_TOKEN_KEY, token)
  } catch {
    // Хранилище закрыто — при выходе отвязывать будет нечего; сервер сам
    // забудет токен, когда Firebase перестанет его принимать.
  }
}

async function send(api: PushApi, token: string, platform: 'android' | 'ios') {
  await api.registerPushToken(token, platform)
  store(token)
}

/** Закрепить токен телефона за вошедшим покупателем. Не ждать: окно разрешения медленное. */
export async function registerPush(api: PushApi): Promise<void> {
  try {
    const push = await nativePushToken()
    if (push) await send(api, push.token, push.platform)
  } catch (error) {
    console.warn('[push] register failed', error)
  }
}

/**
 * Отвязать телефон перед выходом — тем токеном, что закрепляли, и с токеном
 * доступа уходящей сессии. Не дольше PUSH_UNREGISTER_TIMEOUT и без отказов.
 */
export async function unregisterPush(api: PushApi, accessToken: string): Promise<void> {
  const token = stored()
  if (!token) return
  store(null)
  let timer = 0
  try {
    await Promise.race([
      api.unregisterPushToken(token, accessToken),
      new Promise((resolve) => {
        timer = window.setTimeout(resolve, PUSH_UNREGISTER_TIMEOUT)
      }),
    ])
  } catch (error) {
    console.warn('[push] unregister failed', error)
  } finally {
    window.clearTimeout(timer)
  }
}

/** Firebase сменил токен — закрепляем новый. Возвращает отписку. */
export function listenPushToken(api: PushApi): () => void {
  const onChange = (event: Event) => {
    const push = toPushToken((event as CustomEvent<unknown>).detail)
    if (!push) return
    send(api, push.token, push.platform).catch((error: unknown) => {
      console.warn('[push] refresh failed', error)
    })
  }
  window.addEventListener(NATIVE_PUSH_TOKEN_EVENT, onChange)
  return () => {
    window.removeEventListener(NATIVE_PUSH_TOKEN_EVENT, onChange)
  }
}

/** Push пришёл, пока приложение открыто: экран перечитывает то, что он мог изменить. */
export function listenPushReceived(onPush: () => void): () => void {
  window.addEventListener(NATIVE_PUSH_RECEIVED_EVENT, onPush)
  return () => {
    window.removeEventListener(NATIVE_PUSH_RECEIVED_EVENT, onPush)
  }
}
